<script setup lang="ts">
import { computed, ref, onMounted, onUnmounted, nextTick } from 'vue';
import type { AudioClip } from '../api/types';
import { useAudioStore } from '../stores/audioStore';
import { useClipsStore } from '../stores/clipsStore';
import { useAuthStore } from '../stores/authStore';

const props = defineProps<{
  clip: AudioClip;
  padNumber: number;
}>();

const audioStore = useAudioStore();
const clipsStore = useClipsStore();
const authStore = useAuthStore();

// Get reactive clip from store (props.clip may become stale after updates)
const reactiveClip = computed(() => 
  clipsStore.clips.find(c => c.id === props.clip.id) || props.clip
);

// Admin dropdown state
const showActorDropdown = ref(false);
const showTagDropdown = ref(false);
const newActorName = ref('');
const newTagName = ref('');
const dropdownRef = ref<HTMLElement | null>(null);
const actorButtonRef = ref<HTMLElement | null>(null);
const tagButtonRef = ref<HTMLElement | null>(null);

// Title editing state
const isEditingTitle = ref(false);
const editedTitle = ref('');
const titleInputRef = ref<HTMLInputElement | null>(null);
const actorDropdownPosition = ref({ top: 0, left: 0 });
const tagDropdownPosition = ref({ top: 0, left: 0 });

const actorDropdownStyle = computed(() => ({
  top: `${actorDropdownPosition.value.top}px`,
  left: `${actorDropdownPosition.value.left}px`,
}));

const tagDropdownStyle = computed(() => ({
  top: `${tagDropdownPosition.value.top}px`,
  left: `${tagDropdownPosition.value.left}px`,
}));

const isCurrentlyPlaying = computed(() => 
  audioStore.currentClip?.id === props.clip.id && audioStore.isPlaying
);

const progressPercent = computed(() => {
  if (audioStore.currentClip?.id === props.clip.id) {
    return audioStore.progress * 100;
  }
  return 0;
});

// Generate pseudo-random usage bar heights based on clip ID (consistent per clip)
const usageBars = computed(() => {
  const seed = props.clip.id;
  const bars = [];
  for (let i = 0; i < 7; i++) {
    // Pseudo-random using clip ID + bar index
    const rand = Math.sin(seed * 9301 + i * 49297) * 10000;
    const height = 2 + Math.abs(rand % 5); // Heights between 2-6 (in tailwind units)
    bars.push(height);
  }
  return bars;
});

const formattedDuration = computed(() => {
  const seconds = Math.floor(props.clip.duration_ms / 1000);
  const minutes = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${minutes}:${secs.toString().padStart(2, '0')}`;
});

const padLabel = computed(() => {
  const num = props.padNumber;
  return `Pad ${num < 10 ? '0' + num : num}`;
});

// Get all actors for dropdown
const allActors = computed(() => clipsStore.actors);
const allTags = computed(() => clipsStore.tags);

function handleClick() {
  if (isCurrentlyPlaying.value) {
    audioStore.pause();
  } else {
    audioStore.playClip(props.clip);
  }
}

function handleFavoriteClick(event: Event) {
  event.stopPropagation();
  if (authStore.isAuthenticated) {
    clipsStore.toggleFavorite(props.clip.id);
  }
}

function handleMenuClick(event: Event) {
  event.stopPropagation();
  // TODO: Show context menu
}

// Title editing handlers
function startEditingTitle(event: Event) {
  event.stopPropagation();
  if (!authStore.isAdmin) return;
  editedTitle.value = reactiveClip.value.title;
  isEditingTitle.value = true;
  nextTick(() => {
    titleInputRef.value?.focus();
    titleInputRef.value?.select();
  });
}

async function saveTitle() {
  const newTitle = editedTitle.value.trim();
  if (!newTitle || newTitle === reactiveClip.value.title) {
    isEditingTitle.value = false;
    return;
  }
  try {
    await clipsStore.updateClip(props.clip.id, { title: newTitle });
    isEditingTitle.value = false;
  } catch (e) {
    console.error('Failed to update title:', e);
  }
}

function cancelEditingTitle() {
  isEditingTitle.value = false;
  editedTitle.value = '';
}

// Admin actor/tag dropdown handlers
function toggleActorDropdown(event: Event) {
  event.stopPropagation();
  if (!showActorDropdown.value && actorButtonRef.value) {
    const rect = actorButtonRef.value.getBoundingClientRect();
    actorDropdownPosition.value = {
      top: rect.bottom + 4,
      left: rect.left,
    };
  }
  showActorDropdown.value = !showActorDropdown.value;
  showTagDropdown.value = false;
  newActorName.value = '';
}

function toggleTagDropdown(event: Event) {
  event.stopPropagation();
  if (!showTagDropdown.value && tagButtonRef.value) {
    const rect = tagButtonRef.value.getBoundingClientRect();
    tagDropdownPosition.value = {
      top: rect.bottom + 4,
      left: rect.left,
    };
  }
  showTagDropdown.value = !showTagDropdown.value;
  showActorDropdown.value = false;
  newTagName.value = '';
}

async function selectActor(actorId: number) {
  console.log('selectActor called with:', actorId, 'for clip:', props.clip.id);
  try {
    await clipsStore.updateClip(props.clip.id, { actor_id: actorId });
    console.log('Actor update successful');
    showActorDropdown.value = false;
  } catch (e) {
    console.error('Failed to update actor:', e);
  }
}

async function createAndSelectActor() {
  if (!newActorName.value.trim()) return;
  try {
    await clipsStore.createActor(newActorName.value.trim());
    // Find the newly created actor and assign it
    const newActor = clipsStore.actors.find(a => a.name === newActorName.value.trim());
    if (newActor) {
      await selectActor(newActor.id);
    }
    newActorName.value = '';
  } catch (e) {
    console.error('Failed to create actor:', e);
  }
}

async function toggleTag(tagId: number) {
  console.log('toggleTag called with:', tagId);
  const currentTagIds = reactiveClip.value.tags.map(t => t.id);
  console.log('Current tag IDs:', currentTagIds);
  let newTagIds: number[];
  
  if (currentTagIds.includes(tagId)) {
    // Remove tag
    newTagIds = currentTagIds.filter(id => id !== tagId);
    console.log('Removing tag, new IDs:', newTagIds);
  } else {
    // Add tag
    newTagIds = [...currentTagIds, tagId];
    console.log('Adding tag, new IDs:', newTagIds);
  }
  
  try {
    await clipsStore.updateClip(props.clip.id, { tag_ids: newTagIds });
    console.log('Tag update successful');
  } catch (e) {
    console.error('Failed to update tags:', e);
  }
}

async function removeTag(tagId: number, event: Event) {
  event.stopPropagation();
  const newTagIds = reactiveClip.value.tags.filter(t => t.id !== tagId).map(t => t.id);
  try {
    await clipsStore.updateClip(props.clip.id, { tag_ids: newTagIds });
  } catch (e) {
    console.error('Failed to remove tag:', e);
  }
}

async function createAndAddTag() {
  if (!newTagName.value.trim()) return;
  try {
    await clipsStore.createTag(newTagName.value.trim());
    // Find the newly created tag and add it
    const newTag = clipsStore.tags.find(t => t.name === newTagName.value.trim());
    if (newTag) {
      await toggleTag(newTag.id);
    }
    newTagName.value = '';
  } catch (e) {
    console.error('Failed to create tag:', e);
  }
}

// Close dropdowns when clicking outside
function handleClickOutside(event: MouseEvent) {
  const target = event.target as HTMLElement;
  // Check if click is inside any dropdown or trigger button
  const isInsideDropdown = target.closest('.fixed.z-\\[9999\\]');
  const isActorButton = actorButtonRef.value?.contains(target);
  const isTagButton = tagButtonRef.value?.contains(target);
  
  if (!isInsideDropdown && !isActorButton && !isTagButton) {
    showActorDropdown.value = false;
    showTagDropdown.value = false;
  }
}

onMounted(() => {
  document.addEventListener('click', handleClickOutside);
});

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside);
});
</script>

<template>
  <div
    class="relative group cursor-pointer active:scale-95 transition-transform"
    @click="handleClick"
  >
    <div
      :class="[
        'bg-console-panel border rounded-xl p-4 flex flex-col gap-3 min-h-[130px] transition-all',
        isCurrentlyPlaying
          ? 'border-primary border-2 shadow-[0_0_20px_rgba(230,0,119,0.2)]'
          : 'border-console-border hover:border-primary/50 group-hover:shadow-[0_0_15px_rgba(230,0,119,0.1)]'
      ]"
    >
      <!-- Header -->
      <div class="flex justify-between items-start">
        <span
          :class="[
            'text-[10px] font-mono font-bold px-2 py-0.5 rounded uppercase tracking-tighter',
            isCurrentlyPlaying
              ? 'text-white bg-primary animate-pulse'
              : 'text-primary bg-primary/10'
          ]"
        >
          {{ isCurrentlyPlaying ? 'Playing' : padLabel }}
        </span>
        
        <button
          v-if="authStore.isAuthenticated"
          @click="handleFavoriteClick"
          class="transition-colors"
        >
          <span
            :class="[
              'material-symbols-outlined text-xl',
              clip.is_favorite ? 'text-primary fill-1' : 'text-slate-600 hover:text-primary'
            ]"
          >
            favorite
          </span>
        </button>
        <button
          v-else
          @click="handleMenuClick"
          class="material-symbols-outlined text-xl text-slate-600 hover:text-primary transition-colors"
        >
          more_vert
        </button>
      </div>

      <!-- Title & Actor -->
      <div ref="dropdownRef">
        <!-- Title (editable for admin) -->
        <div v-if="isEditingTitle" class="mb-1" @click.stop>
          <input
            ref="titleInputRef"
            v-model="editedTitle"
            class="w-full px-1 py-0.5 text-sm font-bold bg-console-bg border border-primary rounded text-white"
            @keyup.enter="saveTitle"
            @keyup.escape="cancelEditingTitle"
            @blur="saveTitle"
          />
        </div>
        <h3
          v-else
          @click="authStore.isAdmin ? startEditingTitle($event) : null"
          :class="[
            'font-bold text-sm leading-tight line-clamp-2 transition-colors',
            isCurrentlyPlaying ? 'text-primary' : 'group-hover:text-primary',
            authStore.isAdmin ? 'cursor-pointer hover:bg-primary/10 rounded px-1 -mx-1' : ''
          ]"
        >
          {{ reactiveClip.title }}
          <span v-if="authStore.isAdmin" class="material-symbols-outlined text-[10px] opacity-50 align-middle">edit</span>
        </h3>
        
        <!-- Actor (clickable for admin) -->
        <div class="relative">
          <p
            ref="actorButtonRef"
            @click="authStore.isAdmin ? toggleActorDropdown($event) : null"
            :class="[
              'text-[11px] mt-0.5 uppercase tracking-wide truncate',
              isCurrentlyPlaying ? 'text-primary/70' : 'text-slate-500',
              authStore.isAdmin ? 'cursor-pointer hover:text-primary inline-flex items-center gap-1' : ''
            ]"
          >
            {{ reactiveClip.actor.name }}
            <span v-if="authStore.isAdmin" class="material-symbols-outlined text-[10px] opacity-50">edit</span>
          </p>
          
          <!-- Actor Dropdown -->
          <Teleport to="body">
            <div
              v-if="showActorDropdown"
              class="fixed z-[9999] w-48 bg-console-panel border border-console-border rounded-lg shadow-xl max-h-60 overflow-y-auto"
              :style="actorDropdownStyle"
              @click.stop
            >
              <div class="p-2 border-b border-console-border">
                <input
                  v-model="newActorName"
                  placeholder="+ Create new actor..."
                  class="w-full px-2 py-1 text-xs bg-console-bg border border-console-border rounded"
                  @keyup.enter="createAndSelectActor"
                  @click.stop
                />
              </div>
              <div class="py-1">
                <button
                  v-for="actor in allActors"
                  :key="actor.id"
                  @click.stop="selectActor(actor.id)"
                  :class="[
                    'w-full px-3 py-1.5 text-left text-xs hover:bg-primary/20 flex items-center gap-2',
                    reactiveClip.actor.id === actor.id ? 'text-primary bg-primary/10' : 'text-slate-300'
                  ]"
                >
                  <span class="material-symbols-outlined text-sm" v-if="reactiveClip.actor.id === actor.id">check</span>
                  <span v-else class="w-5"></span>
                  {{ actor.name }}
                </button>
              </div>
            </div>
          </Teleport>
        </div>
        
        <!-- Tags (with X for admin to remove) -->
        <div class="relative flex flex-wrap gap-1 mt-1">
          <span
            v-for="tag in reactiveClip.tags.slice(0, 3)"
            :key="tag.id"
            class="text-[9px] px-1.5 py-0.5 bg-cyan-500/10 text-cyan-500/70 rounded inline-flex items-center gap-0.5"
          >
            #{{ tag.name }}
            <button
              v-if="authStore.isAdmin"
              @click="removeTag(tag.id, $event)"
              class="ml-0.5 hover:text-red-400 transition-colors"
              title="Remove tag"
            >
              <span class="material-symbols-outlined text-[10px]">close</span>
            </button>
          </span>
          <button
            v-if="authStore.isAdmin"
            ref="tagButtonRef"
            @click="toggleTagDropdown($event)"
            class="text-[9px] px-1.5 py-0.5 bg-console-bg text-slate-500 rounded hover:text-cyan-400 hover:bg-cyan-500/10"
          >
            +
          </button>
          
          <!-- Tag Dropdown -->
          <Teleport to="body">
            <div
              v-if="showTagDropdown"
              class="fixed z-[9999] w-48 bg-console-panel border border-console-border rounded-lg shadow-xl max-h-60 overflow-y-auto"
              :style="tagDropdownStyle"
              @click.stop
            >
              <div class="p-2 border-b border-console-border">
                <input
                  v-model="newTagName"
                  placeholder="+ Create new tag..."
                  class="w-full px-2 py-1 text-xs bg-console-bg border border-console-border rounded"
                  @keyup.enter="createAndAddTag"
                  @click.stop
                />
              </div>
              <div class="py-1">
                <button
                  v-for="tag in allTags"
                  :key="tag.id"
                  @click.stop="toggleTag(tag.id)"
                  :class="[
                    'w-full px-3 py-1.5 text-left text-xs hover:bg-cyan-500/20 flex items-center gap-2',
                    reactiveClip.tags.some(t => t.id === tag.id) ? 'text-cyan-400 bg-cyan-500/10' : 'text-slate-300'
                  ]"
                >
                  <span class="material-symbols-outlined text-sm" v-if="reactiveClip.tags.some(t => t.id === tag.id)">check</span>
                  <span v-else class="w-5"></span>
                  #{{ tag.name }}
                </button>
              </div>
            </div>
          </Teleport>
        </div>
      </div>

      <!-- Footer -->
      <div class="mt-auto flex items-center justify-between">
        <!-- Usage Graph (mini bar chart) OR Progress Bar when playing -->
        <template v-if="isCurrentlyPlaying">
          <div class="h-1 bg-primary/30 flex-1 rounded-full overflow-hidden mr-2">
            <div
              class="h-full bg-primary transition-all duration-100"
              :style="{ width: `${progressPercent}%` }"
            ></div>
          </div>
        </template>
        <template v-else>
          <div class="flex gap-0.5 items-end">
            <div
              v-for="(height, idx) in usageBars"
              :key="idx"
              class="w-1 bg-primary/70 rounded-full"
              :style="{ height: `${height * 2}px` }"
            ></div>
          </div>
        </template>
        
        <span
          :class="[
            'text-[10px] font-bold',
            isCurrentlyPlaying ? 'text-primary' : 'text-slate-500'
          ]"
        >
          {{ formattedDuration }}
        </span>
      </div>
    </div>
  </div>
</template>
